import { Typography } from "@mui/material";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import {
	useDeleteMcpConnectionMutation,
	useGetMcpConnectionsQuery,
} from "@/pages/Private/redux/mcp/mcp.api";

import { McpGuide } from "./McpGuide";

/**
 * "Verbundene KI-Clients": the AI clients this user let into the scenario editor over MCP, each
 * with "Trennen" — BCM360-383 (decided 2026-10-15). Disconnecting ends its tokens at once.
 */
export const McpConnections: FunctionComponent = () => {
	const { t, i18n } = useTranslation();
	const ts = (key: string, options?: Record<string, string>) =>
		t(`account.mcpConnections.${key}`, options);
	const { data: connections = [], isSuccess } = useGetMcpConnectionsQuery();
	const [disconnect, { isLoading }] = useDeleteMcpConnectionMutation();
	const format = (date: string) =>
		new Intl.DateTimeFormat(i18n.language === "de" ? "de-DE" : "en-US", {
			dateStyle: "medium",
			timeStyle: "short",
		}).format(new Date(date));

	return (
		<div
			className="max-w-[640px] mx-8 mt-6 pt-6 pb-4 px-6 bg-surface border rounded-xl border-border shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]"
			data-testid="mcp-connections"
		>
			<Typography sx={{ color: "text.primary", fontWeight: 500 }} variant="body2">
				{ts("title")}
			</Typography>
			<p className="mt-1 text-sm text-primary-gray-lighter">{ts("description")}</p>
			<ul className="mt-3 mb-2 flex flex-col gap-2">
				{connections.length === 0 && <li className="text-sm text-primary-gray">{ts("empty")}</li>}
				{connections.map((connection) => (
					<li
						key={connection.id}
						className="flex items-center justify-between gap-4 rounded-xl border border-border bg-white px-3 py-2"
					>
						<div>
							<p className="text-sm font-semibold text-primary-gray">{connection.clientName}</p>
							<p className="text-xs text-primary-gray-lighter">
								{ts("connected", { date: format(connection.createdAt) })}
								{connection.lastUsedAt
									? ` · ${ts("lastUsed", { date: format(connection.lastUsedAt) })}`
									: ""}
							</p>
						</div>
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								isLoading={isLoading}
								title={ts("disconnect")}
								type="button"
								onClick={() => disconnect(connection.id)}
							/>
						</div>
					</li>
				))}
			</ul>
			<McpGuide openByDefault={isSuccess && connections.length === 0} />
		</div>
	);
};
